import { useState } from 'react';
import { Copy, Flag, Mail, Send } from 'lucide-react';
import type { Task } from '../types';
import { REPORT_COPY, reportMailto, reportPayload, reportText, SUPPORT_EMAIL } from '../engine/report';
import { platformName } from '../native/native';

/**
 * "Report an AI result" on every expanded task card (App Review 1.2,
 * 17 Oct 2026). Since 18 Oct the primary action is "Send report", which
 * reaches the ZoneIn team from INSIDE the app (Google Play AI-Generated
 * Content policy: reporting "without needing to exit the app") via the
 * `report` function. Email and "Copy report" stay as the fallback when
 * sending fails (CLAUDE.md §1) — and every outcome is shown on the card (§2).
 * Wording and the report body live in engine/report.ts.
 */
export function ReportResultLink({ task }: { task: Task }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const ctx = { platform: platformName() };

  const send = async () => {
    setState('sending');
    setError(null);
    const { sendAiReport } = await import('../sync/firebaseSync');
    const result = await sendAiReport(reportPayload(task, ctx, note));
    if (result.ok) {
      setState('sent');
    } else {
      setState('idle');
      setError(result.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reportText(task, ctx));
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
    setTimeout(() => setCopied(null), 4000);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-brand-600 dark:hover:text-brand-400"
      >
        <Flag size={11} /> {REPORT_COPY.link}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Report an AI result"
      className="space-y-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-2"
    >
      {state === 'sent' ? (
        <p role="status" className="text-[11px] leading-relaxed text-emerald-700 dark:text-emerald-400">
          {REPORT_COPY.sent}
        </p>
      ) : (
        <>
          <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">{REPORT_COPY.intro}</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={2}
            aria-label="What was wrong with this result"
            placeholder={REPORT_COPY.notePlaceholder}
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-base sm:text-xs text-slate-900 dark:text-white placeholder-slate-400"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => void send()}
              disabled={state === 'sending'}
              className="flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-60"
            >
              <Send size={11} /> {state === 'sending' ? REPORT_COPY.sending : REPORT_COPY.send}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
            >
              Close
            </button>
          </div>
          {error && (
            <p role="alert" className="text-[10px] leading-relaxed text-amber-700 dark:text-amber-400">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-slate-500">{REPORT_COPY.orEmail}</span>
            <a
              href={reportMailto(task, ctx)}
              className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-[10px] font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Mail size={10} /> {REPORT_COPY.email}
            </a>
            <button
              onClick={() => void copy()}
              className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-[10px] font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Copy size={10} /> {REPORT_COPY.copy}
            </button>
          </div>
        </>
      )}
      {copied === 'ok' && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
          {REPORT_COPY.copied} {SUPPORT_EMAIL}
        </p>
      )}
      {copied === 'fail' && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400">
          Couldn’t copy on this device — email {SUPPORT_EMAIL} and describe the task.
        </p>
      )}
    </div>
  );
}
